import { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { ZONE_BY_SCAN_CODE } from './zoneByScanCode';
import type { ScannedZone } from './zoneByScanCode';

export type ZoneScanLookupResult = {
  /** The single unambiguous match, or null when the label matched zero or several zones. */
  zone: ScannedZone | null;
  /** Every zone the label could refer to. Length > 1 means the label text is ambiguous. */
  matches: ScannedZone[];
  /** Set when the lookup itself failed (offline, permissions) rather than simply missing. */
  error?: unknown;
};

/**
 * Resolves a scanned zone-label payload against the server. Used by callers that own their
 * own camera and want to interpret a scan as a zone — the picking screens, where the live
 * scanner is otherwise idle. `ZoneScanOverlay` runs the same query behind its own UI.
 */
export function useZoneScanLookup({
  companyId,
  locationId
}: {
  companyId: string;
  locationId?: string;
}) {
  const [lookupZone] = useLazyQuery(ZONE_BY_SCAN_CODE, { fetchPolicy: 'network-only' });

  return useCallback(
    async (code: string): Promise<ZoneScanLookupResult> => {
      try {
        const { data, error } = await lookupZone({
          variables: { companyId, code, locationId }
        });
        if (error) return { zone: null, matches: [], error };
        return {
          zone: data?.zoneByScanCode?.zone || null,
          matches: data?.zoneByScanCode?.matches || []
        };
      } catch (error) {
        return { zone: null, matches: [], error };
      }
    },
    [companyId, locationId, lookupZone]
  );
}

export default useZoneScanLookup;
